import Modal from "~/components/ui/Modal";
import TrialEndedIcon from "~/assets/svg/lock-banners/trial_over_locked.svg";
import CancelledIcon from "~/assets/svg/lock-banners/cancelled_sub.svg";
import ExpiredIcon from "~/assets/svg/lock-banners/subscription_expired.svg";
import PastDueIcon from "~/assets/svg/lock-banners/past_due.svg";
import SuspendedIcon from "~/assets/svg/lock-banners/suspended_icon.svg";
import UnknownIcon from "~/assets/svg/lock-banners/status_unknown.svg";
import * as S from "./styles";
import { SubscriptionLockReason } from "~/types/account";

type LockModalContent = {
  icon: string;
  iconBg: string;
  title: string;
  description: string;
  primaryActionText: string;
  secondaryActionText: string;
};

type SubscriptionLockModalProps = {
  isShowing: boolean;
  reason: Exclude<SubscriptionLockReason, null>;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
};

const lockModalContentByReason: Record<
  Exclude<SubscriptionLockReason, null>,
  LockModalContent
> = {
  trial_ended: {
    icon: TrialEndedIcon,
    iconBg: "#eef0ff",
    title: "Your Trial Has Ended",
    description:
      "Your free trial period has expired. Upgrade to a paid plan to continue using all features of Dropshiptool.",
    primaryActionText: "Upgrade Now",
    secondaryActionText: "View Plans",
  },
  cancelled_period_ended: {
    icon: CancelledIcon,
    iconBg: "#fef1f0",
    title: "Subscription Cancelled",
    description:
      "Your subscription has been cancelled and your access period has ended. Reactivate your subscription to regain access.",
    primaryActionText: "Reactivate Subscription",
    secondaryActionText: "Contact Support",
  },
  subscription_expired: {
    icon: ExpiredIcon,
    iconBg: "#fff9eb",
    title: "Subscription Expired",
    description:
      "Your subscription has expired. Renew your subscription to continue accessing Dropshiptool features.",
    primaryActionText: "Renew Subscription",
    secondaryActionText: "View Plans",
  },
  past_due: {
    icon: PastDueIcon,
    iconBg: "#fef1f0",
    title: "Payment Past Due",
    description:
      "Your last payment failed. Please update your payment method to restore access to your account.",
    primaryActionText: "Update Payment Method",
    secondaryActionText: "Try Again",
  },
  suspended: {
    icon: SuspendedIcon,
    iconBg: "#fef1f0",
    title: "Account Suspended",
    description:
      "Your account has been suspended due to a violation of our terms of service. Contact support for more information.",
    primaryActionText: "Contact Support",
    secondaryActionText: "View Terms",
  },
  billing_status_unknown: {
    icon: UnknownIcon,
    iconBg: "#f4f4f5",
    title: "Billing Status Unknown",
    description:
      "We're unable to verify your billing status. Please try updating your payment method or contact support.",
    primaryActionText: "Update Card",
    secondaryActionText: "Try Again",
  },
};

export const SubscriptionLockModal = ({
  isShowing,
  reason,
  onPrimaryAction,
  onSecondaryAction,
}: SubscriptionLockModalProps) => {
  const content = lockModalContentByReason[reason];

  return (
    <Modal
      id="subscription-lock-modal"
      isShowing={isShowing}
      hideCloseButton
      width={S.modalWidth}
      maxWidth={S.modalWidth}
      minWidth={S.modalWidth}
      padding="40px">
      <S.Wrapper>
        <S.IconCircle bgColor={content.iconBg}>
          <S.IconImage src={content.icon} alt={content.title} />
        </S.IconCircle>
        <S.Title>{content.title}</S.Title>
        <S.Description>{content.description}</S.Description>
        <S.ActionColumn>
          <S.ActionButton type="button" variant="primary" onClick={onPrimaryAction}>
            {content.primaryActionText}
          </S.ActionButton>
          <S.ActionButton
            type="button"
            variant="secondary"
            onClick={onSecondaryAction}>
            {content.secondaryActionText}
          </S.ActionButton>
        </S.ActionColumn>
      </S.Wrapper>
    </Modal>
  );
};

